import React, { useMemo } from 'react';

import { Button, Card, Flex, Input, InputNumber, Select, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { FormItem, FormLabel } from 'src/pages/observers/components/styles';
import ReportPreview from 'src/pages/observers/components/ReportPreview';
import type {
	IQueryFieldsItem,
	ReportBlock,
	ReportBlockType,
	ReportJoin
} from 'src/interfaces/entities/observer';

interface IReportSource {
	key: string
	fields: IQueryFieldsItem[]
}

interface IReportBlocksEditorComponent {
	/** Consultas disponíveis (no observador comum: só 'resultado') */
	sources: IReportSource[]
	blocks: ReportBlock[]
	onBlocksChange: (blocks: ReportBlock[]) => void
	/** Presente só no grupo de consultas: habilita os cruzamentos */
	joins?: ReportJoin[]
	onJoinsChange?: (joins: ReportJoin[]) => void
	/** Mostra o preview ao lado, com a amostra do teste de SQL por fonte (memoize no pai) */
	preview?: { title: string, data: Record<string, Record<string, unknown>[]> }
}

const BLOCK_TYPES: { value: ReportBlockType, label: string }[] = [
	{ value: 'table', label: 'Tabela', },
	{ value: 'bar', label: 'Gráfico de barras', },
	{ value: 'line', label: 'Gráfico de linhas', },
	{ value: 'pie', label: 'Gráfico de pizza', },
];

// description aparece no dropdown, embaixo de cada opção
const JOIN_TYPES = [
	{
		value: 'inner',
		label: 'Só as linhas que casarem',
		description: 'Mantém só o que existe nas duas consultas. Ex.: um mês com venda mas sem meta fica de fora.',
	},
	{
		value: 'left',
		label: 'Todas as linhas da 1ª consulta',
		description: 'Mantém tudo da 1ª consulta; sem par na 2ª, as colunas dela ficam vazias. '
			+ 'Ex.: um mês com venda e sem meta aparece com a meta em branco.',
	},
];

// Colunas do cruzamento ficam prefixadas pela consulta de origem (ex.: vendas.total)
export const getJoinFields = (join: ReportJoin, sources: IReportSource[]): IQueryFieldsItem[] => [
	join.left,
	join.right,
].flatMap((key) => (sources.find((source) => source.key === key)?.fields ?? [])
	.map((field) => ({ ...field, field: `${key}.${field.field}`, })));

const toOptions = (fields: IQueryFieldsItem[]) => fields.map(({ field, }) => ({ value: field, label: field, }));

const Label = ({ text, tooltip, }: { text: string, tooltip?: string }) => (
	<FormLabel>
		{text}
		{tooltip && <Tooltip title={tooltip}>
			<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help', }} />
		</Tooltip>}
	</FormLabel>
);

export default function ReportBlocksEditor(
	{
		sources,
		blocks,
		onBlocksChange,
		joins,
		onJoinsChange,
		preview,
	}: IReportBlocksEditorComponent
) {
	const allSources = useMemo<IReportSource[]>(() => [
		...sources,
		...(joins ?? []).map((join) => ({ key: join.key, fields: getJoinFields(join, sources), })),
	], [ sources, joins, ]);

	const updateJoin = (index: number, patch: Partial<ReportJoin>) =>
		onJoinsChange?.((joins ?? []).map((join, i) => (i === index ? { ...join, ...patch, } : join)));

	const updateBlock = (index: number, patch: Partial<ReportBlock>) =>
		onBlocksChange(blocks.map((block, i) => (i === index ? { ...block, ...patch, } : block)));

	const fieldsOf = (key?: string) => allSources.find((source) => source.key === key)?.fields ?? [];

	return (
		<Flex wrap gap={24} align='start'>
			<Flex vertical gap={16} style={{ flex: '1 1 480px', minWidth: 0, }}>
				{
					onJoinsChange && <FormItem>
						<Label text='Cruzamentos:' />
						<Typography.Paragraph type='secondary' style={{ marginBottom: 0, }}>
						Junta duas consultas numa tabela só, linha a linha, pela coluna que elas têm em comum.
						Ex.: <b>vendas</b> (mes, total) + <b>metas</b> (mes, valor) ligadas por <b>mes</b> viram uma
						tabela com <i>vendas.mes, vendas.total, metas.mes, metas.valor</i> — e um único gráfico pode
						mostrar vendas × meta por mês. Use o identificador do cruzamento como fonte de um bloco.
						Se o valor da coluna se repetir na 2ª consulta, a linha da 1ª aparece uma vez para cada par.
						</Typography.Paragraph>
						{(joins ?? []).map((join, index) => (
							<Card key={index} size='small'>
								<Flex wrap gap={8} align='center'>
									<Input
										style={{ width: 160, }}
										placeholder='identificador'
										value={join.key}
										onChange={(e) => updateJoin(index, {
											key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
										})}
									/>
									<Select
										style={{ width: 140, }}
										placeholder='Consulta 1'
										value={join.left || undefined}
										options={sources.map(({ key, }) => ({ value: key, label: key, }))}
										onChange={(left) => updateJoin(index, { left, leftOn: '', })}
									/>
									<Select
										style={{ width: 140, }}
										placeholder='Coluna'
										value={join.leftOn || undefined}
										options={toOptions(fieldsOf(join.left))}
										onChange={(leftOn) => updateJoin(index, { leftOn, })}
									/>
									<Typography.Text type='secondary'>=</Typography.Text>
									<Select
										style={{ width: 140, }}
										placeholder='Consulta 2'
										value={join.right || undefined}
										options={sources.map(({ key, }) => ({ value: key, label: key, }))}
										onChange={(right) => updateJoin(index, { right, rightOn: '', })}
									/>
									<Select
										style={{ width: 140, }}
										placeholder='Coluna'
										value={join.rightOn || undefined}
										options={toOptions(fieldsOf(join.right))}
										onChange={(rightOn) => updateJoin(index, { rightOn, })}
									/>
									<Select
										style={{ width: 230, }}
										popupMatchSelectWidth={360}
										value={join.type}
										options={JOIN_TYPES}
										optionRender={(option) => (
											<div style={{ whiteSpace: 'normal', }}>
												<div>{option.data.label}</div>
												<Typography.Text type='secondary' style={{ fontSize: 12, }}>
													{option.data.description}
												</Typography.Text>
											</div>
										)}
										onChange={(type) => updateJoin(index, { type, })}
									/>
									<Button
										type='text'
										danger
										icon={<DeleteOutlined />}
										onClick={() => onJoinsChange((joins ?? []).filter((_, i) => i !== index))}
									/>
								</Flex>
								<Typography.Text type='secondary' style={{ fontSize: 12, }}>
								Colunas geradas: {fieldsOf(join.key).map(({ field, }) => field).join(', ') || '—'}
								</Typography.Text>
							</Card>
						))}
						<Button
							icon={<PlusOutlined />}
							disabled={sources.length < 2}
							onClick={() => onJoinsChange([
								...(joins ?? []),
								{
									key: `cruzamento_${(joins?.length ?? 0) + 1}`,
									left: sources[0]?.key ?? '',
									leftOn: '',
									right: sources[1]?.key ?? '',
									rightOn: '',
									type: 'inner',
								},
							])}
						>
						Adicionar cruzamento
						</Button>
					</FormItem>
				}
				<FormItem>
					<Label
						text='Blocos do relatório:'
						tooltip='Gráficos e tabelas do anexo HTML enviado junto com a mensagem. Agregações (soma, média...) devem ser feitas no próprio SQL.'
					/>
					{blocks.map((block, index) => {
						const fieldOptions = toOptions(fieldsOf(block.source));
						const isTable = block.type === 'table';

						return (
							<Card
								key={index}
								size='small'
								title={`Bloco ${index + 1}`}
								extra={<Button
									type='text'
									danger
									icon={<DeleteOutlined />}
									onClick={() => onBlocksChange(blocks.filter((_, i) => i !== index))}
								/>}
							>
								<Flex wrap gap={8}>
									<Select
										style={{ width: 180, }}
										value={block.type}
										options={BLOCK_TYPES}
										onChange={(type) => updateBlock(index, {
											type,
											values: type === 'pie' ? block.values?.slice(0, 1) : block.values,
										})}
									/>
									{
										allSources.length > 1 && <Select
											style={{ width: 180, }}
											placeholder='Fonte de dados'
											value={block.source || undefined}
											options={allSources.map(({ key, }) => ({ value: key, label: key, }))}
											onChange={(source) => updateBlock(index, {
												source,
												columns: [],
												label: undefined,
												values: [],
											})}
										/>
									}
									<Input
										style={{ width: 240, }}
										placeholder='Título (opcional)'
										value={block.title}
										onChange={(e) => updateBlock(index, { title: e.target.value, })}
									/>
								</Flex>
								<Flex wrap gap={8} style={{ marginTop: 8, }}>
									{
										isTable ? <>
											<Select
												mode='multiple'
												allowClear
												style={{ minWidth: 300, flex: 1, }}
												placeholder='Colunas (vazio = todas)'
												value={block.columns}
												options={fieldOptions}
												onChange={(columns) => updateBlock(index, { columns, })}
											/>
											<InputNumber
												min={1}
												max={500}
												addonBefore='Linhas'
												value={block.limit ?? 20}
												onChange={(limit) => updateBlock(index, { limit: limit ?? 20, })}
											/>
										</> : <>
											<Select
												style={{ width: 220, }}
												placeholder={block.type === 'pie' ? 'Rótulo das fatias' : 'Rótulo (eixo X)'}
												value={block.label}
												options={fieldOptions}
												onChange={(label) => updateBlock(index, { label, })}
											/>
											<Select
												mode='multiple'
												style={{ minWidth: 300, flex: 1, }}
												placeholder={block.type === 'pie' ? 'Valor' : 'Valores (até 8 séries)'}
												maxCount={block.type === 'pie' ? 1 : 8}
												value={block.values}
												options={fieldOptions}
												onChange={(values) => updateBlock(index, { values, })}
											/>
										</>
									}
								</Flex>
							</Card>
						);
					})}
					<Button
						icon={<PlusOutlined />}
						disabled={!allSources.length}
						onClick={() => onBlocksChange([
							...blocks,
							{ type: 'table', source: allSources[allSources.length - 1]?.key ?? '', limit: 20, },
						])}
					>
					Adicionar bloco
					</Button>
				</FormItem>
			</Flex>
			{
				preview && <div style={{ flex: '1 1 420px', minWidth: 0, }}>
					<ReportPreview title={preview.title} blocks={blocks} joins={joins} data={preview.data} />
				</div>
			}
		</Flex>
	);
}
